import { Avatar } from "@mantine/core";
import { type ColumnDef } from "@tanstack/react-table";

import { type RewardedLens } from "../../../../gql-generated/types";
import { formatMilliUsd } from "../../../../lens-creator-payout/utils/formatMilliUsd";
import { formatNullableNumber, formatWithUSFormat } from "../../../../lens-stats/formatters/formatNumbers";
import { truncate } from "../../../../shared/styles";
import {
    ColumnIcon,
    ColumnName,
    ColumnPayout,
    ColumnPayoutCount,
    ColumnPayoutScore,
    ColumnSubscriberActions,
    ColumnSubscriberSignups,
} from "../messages.generated";

import { RewardedLensActions } from "./RewardedLensActions";

const P300 = "paragraph-p300";

export const COLUMN_SELECT = "select";
export const COLUMN_ICON = "icon";
export const COLUMN_NAME = "name";
export const COLUMN_PAYOUT = "payout";
export const COLUMN_PAYOUT_COUNT = "payoutCount";
export const COLUMN_SUBSCRIBER_ACTIONS = "subscriberActions";
export const COLUMN_SUBSCRIBER_SIGNUPS = "subscriberSignups";
export const COLUMN_PAYOUT_SCORE = "payoutScore";
export const COLUMN_ACTIONS = "actions";

export const rewardedLensesColumns: ColumnDef<RewardedLens>[] = [
    {
        id: COLUMN_ICON,
        header: () => <ColumnIcon />,
        cell: ({ row }) => <Avatar src={row.original.lens?.iconUrl} size={36} radius="xl" />,
        size: 60,
        enableSorting: false,
    },
    {
        id: COLUMN_NAME,
        header: () => <ColumnName />,
        accessorFn: (row) => row.lens?.name ?? row.unlockableId,
        cell: ({ getValue }) => <div className={`${P300} ${truncate}`}>{getValue() as string}</div>,
        size: 240,
    },
    {
        id: COLUMN_PAYOUT,
        header: () => <ColumnPayout />,
        accessorFn: (row) => row.totalEarningsMilliUsd,
        cell: ({ row }) => (
            <span className={`${P300} ${truncate}`}>{formatMilliUsd(row.original.totalEarningsMilliUsd)}</span>
        ),
        size: 120,
    },
    {
        id: COLUMN_PAYOUT_COUNT,
        header: () => <ColumnPayoutCount />,
        accessorFn: (row) => row.totalPayoutCount,
        cell: ({ row }) => (
            <span className={`${P300} ${truncate}`}>{formatWithUSFormat(row.original.totalPayoutCount)}</span>
        ),
        size: 120,
    },
    {
        id: COLUMN_SUBSCRIBER_ACTIONS,
        header: () => <ColumnSubscriberActions />,
        accessorFn: (row) => row.totalSubscriberUniqueUsers ?? 0,
        cell: ({ row }) => (
            <span className={`${P300} ${truncate}`}>
                {formatNullableNumber(row.original.totalSubscriberUniqueUsers)}
            </span>
        ),
        size: 150,
    },
    {
        id: COLUMN_SUBSCRIBER_SIGNUPS,
        header: () => <ColumnSubscriberSignups />,
        accessorFn: (row) => row.lensPlusSignups.settled,
        cell: ({ row }) => (
            <span className={`${P300} ${truncate}`}>{formatWithUSFormat(row.original.lensPlusSignups.settled)}</span>
        ),
        size: 130,
    },
    {
        id: COLUMN_PAYOUT_SCORE,
        header: () => <ColumnPayoutScore />,
        accessorFn: (row) => row.lensPlusScore ?? 0,
        cell: ({ row }) => (
            <span className={`${P300} ${truncate}`}>{formatNullableNumber(row.original.lensPlusScore)}</span>
        ),
        size: 120,
    },
    {
        id: COLUMN_ACTIONS,
        header: () => null,
        cell: ({ row }) => <RewardedLensActions row={row.original} />,
        size: 120,
        enableSorting: false,
    },
];
